const axiosModule = require('axios')
const axios = axiosModule.default || axiosModule

let embedToken = ''

function setEmbedToken(token) {
  if (typeof token !== 'string' || !token.trim()) throw new Error('组件令牌不正确')
  embedToken = token
}

function clearEmbedToken() {
  embedToken = ''
}

function isEmbedMode() {
  return Boolean(embedToken)
}

function buildEmbedRequest(path, config = {}) {
  if (!embedToken) throw new Error('组件令牌尚未就绪')
  return {
    ...config,
    url: `/embed-api/v1${path}`,
    headers: {
      ...(config.headers || {}),
      Authorization: `Bearer ${embedToken}`
    }
  }
}

async function embedRequest(path, config = {}) {
  try {
    const response = await axios({
      baseURL: process.env.VUE_APP_BASE_API || '',
      timeout: 30000,
      ...buildEmbedRequest(path, config)
    })
    const body = response.data || {}
    if (!body.success) throw new Error(body.message || '组件请求失败')
    return { success: true, code: 200, msg: body.message || '操作成功', data: body.data }
  } catch (error) {
    const body = error.response && error.response.data
    const wrapped = new Error((body && (body.message || body.msg)) || error.message || '组件请求失败')
    wrapped.code = body && body.code
    wrapped.response = error.response
    throw wrapped
  }
}

function toLonlat(longitude, latitude) {
  return longitude === null || longitude === undefined ||
    latitude === null || latitude === undefined ? null : `${longitude},${latitude}`
}

function toInternalStopover(value = {}) {
  return {
    stopoverSeq: value.sequence,
    stopoverAddress: value.address,
    stopoverLonlat: toLonlat(value.longitude, value.latitude),
    planArriveTime: value.planArriveTime,
    stopoverRadius: value.fenceRadius
  }
}

function toInternalWaybill(value = {}) {
  return {
    ...value,
    startLonlat: toLonlat(value.startLongitude, value.startLatitude),
    endLonlat: toLonlat(value.endLongitude, value.endLatitude),
    waypointFenceCode: Array.isArray(value.waypointFenceCodes)
      ? value.waypointFenceCodes.join(',') : '',
    createTime: value.createdAt,
    updateTime: value.updatedAt,
    stopovers: Array.isArray(value.stopovers)
      ? value.stopovers.map(toInternalStopover) : []
  }
}

function toInternalPosition(value) {
  if (!value) return null
  return {
    waybillNo: value.waybillNo,
    vehicleNo: value.vehicleNo,
    plateColor: value.plateColor,
    currentLongitude: value.longitude,
    currentLatitude: value.latitude,
    currentAddress: value.address,
    locationTime: value.locationTime,
    currentSpeed: value.speed,
    currentDirection: value.direction,
    currentMileage: value.mileage,
    monitorStatus: value.monitorStatus,
    syncStatus: value.syncStatus,
    currentAddressSource: value.addressSource,
    drivenDistance: value.drivenDistance,
    remainingDistance: value.remainingDistance,
    remainingDistanceSource: value.remainingDistanceSource,
    estimatedArriveTime: value.estimatedArriveTime,
    estimatedArriveSource: value.estimatedArriveSource,
    offlineStatus: value.offlineStatus,
    offlineTime: value.offlineTime
  }
}

function toInternalTrackPoint(value = {}) {
  return {
    longitude: value.longitude,
    latitude: value.latitude,
    speed: value.speed,
    direction: value.direction,
    mileage: value.mileage,
    altitude: value.altitude,
    locationTime: value.locationTime,
    locationName: value.address,
    province: value.province,
    city: value.city,
    county: value.county
  }
}

function toInternalTrackPage(value = {}) {
  const tracks = Array.isArray(value.items) ? value.items.map(toInternalTrackPoint) : []
  const monitor = toInternalPosition(value.currentPosition)
  const currentTrack = tracks.length ? tracks[tracks.length - 1] : (monitor ? {
    vehicleNo: monitor.vehicleNo,
    longitude: monitor.currentLongitude,
    latitude: monitor.currentLatitude,
    locationName: monitor.currentAddress,
    locationTime: monitor.locationTime,
    speed: monitor.currentSpeed,
    direction: monitor.currentDirection,
    mileage: monitor.currentMileage
  } : null)
  return {
    monitor,
    tracks,
    currentTrack,
    nextCursor: value.nextAfterTime ? Date.parse(value.nextAfterTime) : null,
    nextBackfillCursor: value.nextBackfillId,
    hasMore: value.hasMore === true,
    backfillHasMore: value.backfillHasMore === true,
    lastSuccessTime: value.lastSuccessTime
  }
}

function toInternalRoute(value) {
  if (!value) return null
  return {
    id: value.routeId,
    routeName: value.routeName,
    routeSource: value.routeSource,
    externalRouteId: value.externalRouteId,
    coordinateSystem: value.coordinateSystem,
    startAddress: value.startAddress,
    endAddress: value.endAddress,
    routeList: (value.points || []).map(point => `${point.longitude},${point.latitude}`).join('|'),
    totalDileage: value.totalMileage,
    totalDuration: value.totalDuration,
    status: value.status,
    createTime: value.createdAt,
    updateTime: value.updatedAt
  }
}

function toInternalFence(value = {}) {
  return {
    fenceCode: value.fenceCode,
    fenceName: value.fenceName,
    fenceType: value.fenceType,
    fenceCoords: value.coordinates,
    fenceRadius: value.radius,
    coordType: value.coordinateType,
    status: value.status,
    effectiveStartTime: value.effectiveStartTime,
    effectiveEndTime: value.effectiveEndTime
  }
}

function toInternalDetail(value = {}) {
  const waybill = toInternalWaybill(value.waybill || {})
  return {
    waybill,
    stopovers: waybill.stopovers || [],
    monitor: toInternalPosition(value.position),
    route: toInternalRoute(value.route),
    fences: Array.isArray(value.fences) ? value.fences.map(toInternalFence) : []
  }
}

function toInternalAlarm(value = {}) {
  return {
    ...value,
    alarmLonlat: toLonlat(value.longitude, value.latitude),
    alarmAddress: value.address,
    alarmRemark: value.detail,
    handleTime: value.handledAt,
    createTime: value.createdAt
  }
}

function toInternalAreaVehicle(value = {}) {
  return {
    waybillNo: value.waybillNo,
    vehicleNo: value.vehicleNo,
    plateColor: value.plateColor,
    currentLongitude: value.longitude,
    currentLatitude: value.latitude,
    currentAddress: value.address,
    locationTime: value.locationTime,
    currentSpeed: value.speed
  }
}

function readTokenClaims(token = embedToken) {
  try {
    const payload = token.split('.')[1]
    if (!payload) return {}
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = normalized + '='.repeat((4 - normalized.length % 4) % 4)
    const json = decodeURIComponent(Array.from(atob(padded), char =>
      `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`).join(''))
    return JSON.parse(json)
  } catch (error) {
    return {}
  }
}

module.exports = {
  buildEmbedRequest,
  clearEmbedToken,
  embedRequest,
  isEmbedMode,
  readTokenClaims,
  setEmbedToken,
  toInternalAlarm,
  toInternalAreaVehicle,
  toInternalDetail,
  toInternalPosition,
  toInternalRoute,
  toInternalTrackPage,
  toInternalWaybill
}
